import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { LoaderCircle, Square, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { trackHubEvent } from "@/utils/hub";
import { devError } from "@/utils/logger";
import { transientSession } from "@/utils/preferences";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "iwc_tts_session";
const NarrationContext = createContext(null);
const IDLE_STATE = { activeId: null, label: "", status: "idle" };
let inMemoryNarrationSession = "";

function narrationSessionId() {
  try {
    let id = transientSession.get(SESSION_KEY);
    if (!id) {
      id = `tts-${crypto.randomUUID()}`;
      transientSession.set(SESSION_KEY, id);
    }
    return id;
  } catch (error) {
    devError("Narration session storage is unavailable; using an in-memory ID:", error);
    if (!inMemoryNarrationSession) inMemoryNarrationSession = `tts-${crypto.randomUUID()}`;
    return inMemoryNarrationSession;
  }
}

export function NarrationProvider({ children }) {
  const [state, setState] = useState(IDLE_STATE);
  const audioRef = useRef(null);
  const abortRef = useRef(null);
  const objectUrlRef = useRef("");
  const metadataRef = useRef(null);

  const releaseAudioUrl = useCallback(() => {
    if (!objectUrlRef.current) return;
    URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = "";
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
    }
    releaseAudioUrl();
    metadataRef.current = null;
    setState(IDLE_STATE);
  }, [releaseAudioUrl]);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = "auto";
    audioRef.current = audio;
    const onEnded = () => {
      const metadata = metadataRef.current;
      if (metadata) trackHubEvent("tts_complete", metadata.contentId, { context: metadata.context });
      releaseAudioUrl();
      metadataRef.current = null;
      setState(IDLE_STATE);
    };
    const onError = () => {
      const metadata = metadataRef.current;
      if (metadata) trackHubEvent("tts_error", metadata.contentId, { context: metadata.context, stage: "playback" });
      toast.error("Narration playback was interrupted. Please try again.");
      releaseAudioUrl();
      metadataRef.current = null;
      setState((current) => ({ ...current, status: "error" }));
    };
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.pause();
      releaseAudioUrl();
      abortRef.current?.abort();
    };
  }, [releaseAudioUrl]);

  const play = useCallback(async ({ contentId = null, context, id, label, text }) => {
    if (!text?.trim()) return;
    if (state.activeId === id && ["loading", "playing"].includes(state.status)) {
      stop();
      return;
    }
    stop();
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ activeId: id, label, status: "loading" });
    try {
      const response = await fetch(`${API}/tts/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, context, sessionId: narrationSessionId() }),
        signal: controller.signal,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const error = new Error(payload.detail || "Narration is unavailable.");
        error.status = response.status;
        throw error;
      }
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      metadataRef.current = { contentId, context };
      audioRef.current.src = objectUrl;
      await audioRef.current.play();
      setState({ activeId: id, label, status: "playing" });
      trackHubEvent("tts_play", contentId, { context });
    } catch (error) {
      if (error.name === "AbortError") return;
      trackHubEvent("tts_error", contentId, { context, stage: "generation", status: error.status || 0 });
      setState({ activeId: id, label, status: "error" });
      if (error.status === 429) toast.error("Narration limit reached", { description: error.message });
      else toast.error("Narration unavailable", { description: error.message });
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [state.activeId, state.status, stop]);

  const contextValue = useMemo(() => ({ ...state, play, stop }), [play, state, stop]);

  return <NarrationContext.Provider value={contextValue}>
    {children}
    {state.activeId && ["loading", "playing"].includes(state.status) && <div className="fixed left-4 top-20 z-[70] flex max-w-[calc(100vw-2rem)] items-center gap-3 border-2 border-navy bg-white px-4 py-3 shadow-[6px_6px_0_rgba(17,41,107,0.18)] sm:bottom-4 sm:top-auto" role="status" data-testid="global-narration-player">
      {state.status === "loading" ? <LoaderCircle className="h-4 w-4 animate-spin text-brand-red" /> : <Volume2 className="h-4 w-4 text-brand-red" />}
      <div className="min-w-0"><p className="truncate font-mono text-[10px] font-bold uppercase text-navy">{state.status === "loading" ? "Preparing audio" : "Playing narration"}</p><p className="truncate font-body text-xs text-gray-500" data-testid="global-narration-label">{state.label}</p></div>
      <button type="button" onClick={stop} aria-label="Stop narration" className="ml-2 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center bg-navy text-white transition-colors hover:bg-navy-dark" data-testid="global-narration-stop"><Square className="h-3.5 w-3.5 fill-current" /></button>
    </div>}
  </NarrationContext.Provider>;
}

export function useNarration() {
  const context = useContext(NarrationContext);
  if (!context) throw new Error("useNarration must be used inside NarrationProvider");
  return context;
}